"use client";

import * as React from "react";
import { motion, useMotionValue, useTransform, PanInfo } from "framer-motion";
import { cn } from "@/lib/utils";
import { useMobileNavigation } from "./MobileNavigationContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { ChevronLeft } from "lucide-react";

interface SwipeNavigationProps {
  children: React.ReactNode;
  className?: string;
  enabled?: boolean;
  threshold?: number;
  edgeWidth?: number;
}

export function SwipeNavigation({
  children,
  className,
  enabled = true,
  threshold = 100,
  edgeWidth = 30,
}: SwipeNavigationProps) {
  const isMobile = useIsMobile();
  const { canGoBack, goBack, isMenuOpen, isSearchOpen } = useMobileNavigation();
  const x = useMotionValue(0);
  const [isSwipeActive, setIsSwipeActive] = React.useState(false);
  const [startedFromEdge, setStartedFromEdge] = React.useState(false);

  // Transform for visual feedback
  const backgroundOpacity = useTransform(x, [0, threshold], [0, 0.3]);
  const indicatorScale = useTransform(x, [0, threshold], [0.5, 1]);
  const indicatorOpacity = useTransform(x, [0, threshold / 2, threshold], [0, 0.5, 1]);
  const indicatorX = useTransform(x, [0, threshold], [-20, 20]);
  const contentX = useTransform(x, [0, threshold], [0, 20]);

  // Don't enable swipe when modals are open
  const swipeEnabled = enabled && isMobile && canGoBack && !isMenuOpen && !isSearchOpen;

  const handlePanStart = React.useCallback(
    (event: MouseEvent | TouchEvent | PointerEvent) => {
      if (!swipeEnabled) return;

      // Check if pan started from the left edge
      const clientX =
        "touches" in event
          ? event.touches[0].clientX
          : (event as MouseEvent).clientX;

      if (clientX <= edgeWidth) {
        setStartedFromEdge(true);
        setIsSwipeActive(true);
      }
    },
    [swipeEnabled, edgeWidth]
  );

  const handlePan = React.useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (!startedFromEdge) return;

      // Only allow right swipe (positive offset)
      const offset = Math.max(0, info.offset.x);
      x.set(offset);
    },
    [startedFromEdge, x]
  );

  const handlePanEnd = React.useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (!startedFromEdge) {
        x.set(0);
        setIsSwipeActive(false);
        setStartedFromEdge(false);
        return;
      }

      const offset = info.offset.x;
      const velocity = info.velocity.x;

      // Trigger navigation if threshold met or high velocity
      if (offset > threshold || velocity > 500) {
        // Haptic feedback
        if ("vibrate" in navigator) {
          navigator.vibrate(10);
        }
        goBack();
      }

      // Reset
      x.set(0);
      setIsSwipeActive(false);
      setStartedFromEdge(false);
    },
    [startedFromEdge, threshold, goBack, x]
  );

  if (!swipeEnabled) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={cn("relative touch-pan-y", className)}
      onPanStart={handlePanStart}
      onPan={handlePan}
      onPanEnd={handlePanEnd}
      style={{ x: 0 }} // Prevent actual movement
    >
      {/* Edge indicator for swipe-to-go-back */}
      <motion.div
        className={cn(
          "fixed left-0 top-0 bottom-0 w-12 z-[100] pointer-events-none",
          "flex items-center justify-center"
        )}
        style={{ opacity: indicatorOpacity }}
        aria-hidden="true"
      >
        <motion.div
          className={cn(
            "w-10 h-10 rounded-full",
            "bg-background shadow-lg border border-border",
            "flex items-center justify-center"
          )}
          style={{
            scale: indicatorScale,
            x: indicatorX,
          }}
        >
          <ChevronLeft className="w-6 h-6 text-foreground" />
        </motion.div>
      </motion.div>

      {/* Background overlay during swipe */}
      <motion.div
        className="fixed inset-0 bg-black pointer-events-none z-[99]"
        style={{ opacity: backgroundOpacity }}
        aria-hidden="true"
      />

      {/* Content */}
      <motion.div
        style={{
          x: contentX,
        }}
      >
        {children}
      </motion.div>

      {/* Swipe edge touch target (invisible) */}
      {canGoBack && (
        <div
          className="fixed left-0 top-16 bottom-20 w-6 z-[98]"
          aria-hidden="true"
        />
      )}
    </motion.div>
  );
}

export default SwipeNavigation;
